/**
 * The ink on this board, and the stroke currently under the hand.
 *
 * The rule this file exists to keep: ink never waits for the network
 * (docs/03-ARCHITECTURE.md §7.2). A point goes straight into the stroke being drawn
 * and onto the active layer. Sharing and saving are added later, above this, never
 * in front of it.
 *
 * The finished stroke lands on the static layer, so the next pen movement repaints
 * only the thin active layer. That layer keeps highlighter ink and pen ink in two
 * bands, because SRS C4 says highlighter always sits underneath.
 */

import type { CanvasLayerName, ViewportState } from '@opal/contracts';
import type { BoardObject, Rect } from '@opal/shared';
import {
  newStrokeId,
  screenWidthToCanvasWidth,
  type Layer,
  type Stroke,
  type StrokeTool,
} from '@opal/shared';

import {
  findStroke,
  land,
  newBands,
  newIndex,
  removeStrokeFrom,
  replaceStrokeIn,
  type BoardInk,
} from './ink-board.js';
import { clearCanvas, drawInk, isFlatTool, type InkPoint } from './ink.js';
import {
  emptyBoardMemory,
  paintBoard,
  planActivePaint,
  strokesIn,
  viewportKey,
  type BoardMemory,
  type PaintMemory,
} from './ink-paint.js';
import { createPressureReader, type PressureReader } from './pressure.js';
import { duplicateStroke, type Copied, type Duplicated } from './selection.js';
import { toViewport } from './viewport-state.js';

/** What the teacher is holding: read fresh each time the pen goes down. */
export interface PenChoice {
  readonly tool: StrokeTool;
  readonly color: string;
  /** SRS C4 — 1 for the pen, see-through for the highlighter. */
  readonly opacity: number;
}

export interface InkSessionOptions {
  readonly userId: string;
  /**
   * SRS C3 — the pen holds a target SCREEN width; the stroke stores canvas width.
   * Read fresh at every stroke, so moving the slider takes effect on the next line.
   */
  readonly targetScreenWidth: () => number;
  /** The tool and colour in hand. Also read fresh, for the same reason. */
  readonly pen: () => PenChoice;
  /** SRS C8 — which layer new work goes on. Read the moment the hand goes down. */
  readonly layer?: () => Layer;
  /**
   * SRS C5 — smart ink smoothing, run once as the hand lifts.
   *
   * Passed in rather than done here, because whether ink is smoothed is a Smart Pen
   * setting and the pen engine has no business knowing that the Smart Pen exists.
   * Given nothing, the stroke is stored exactly as it was drawn.
   */
  readonly smooth?: (points: readonly InkPoint[]) => readonly InkPoint[];
  /** Tells the board which canvas layer now needs repainting. */
  readonly onChanged: (layer: CanvasLayerName) => void;
}

/** One reading from the device: a canvas point, its raw pressure, and when it arrived. */
export interface InkSample extends InkPoint {
  /** Milliseconds, taken from the pointer event itself, not from a clock. */
  readonly at: number;
}

export interface InkSession {
  /** A pointer went down. */
  begin(sample: InkSample, viewport: ViewportState): void;
  /**
   * Takes a stroke back off the board - an undo, here or on somebody else's screen.
   *
   * False if it was not there, which is normal: two people can undo the same thing at
   * once, and the second one has nothing left to do.
   */
  remove(strokeId: string): boolean;
  /**
   * Swaps a stroke for a changed version of itself, keeping its place on the board.
   *
   * How a moved, resized or recoloured stroke lands (SRS C4). The record is frozen
   * (docs/05-DATA-MODEL.md §3.1), so a change is always a different record carrying
   * the same id — and keeping the id is what keeps everyone's board in step and the
   * ink in the order it was drawn.
   *
   * False when there is nothing here with that id.
   */
  replace(stroke: Stroke): boolean;
  /**
   * Puts a stroke somebody else drew onto the board.
   *
   * The same landing as a stroke drawn here, so remote ink is indistinguishable from
   * local ink once it is down.
   */
  accept(stroke: Stroke): void;
  /**
   * A second copy of a stroke, offset, already on the board.
   *
   * Ink can always be copied, and a copy of ink is ink — which is why this can answer
   * for itself. What is on the board beside it may not be (board-items.ts).
   */
  duplicate(stroke: Stroke, offset: number): Duplicated;
  /** §16 — a stroke lifted off the board, for the clipboard to hold. */
  copyOf(strokeId: string): Copied | null;
  /** §16 — a held copy put back on the board, offset. Always ink, and ink can land. */
  place(item: Copied, offset: number): Duplicated | null;
  /** Everything on the page, for Ctrl+A. */
  all(): readonly Stroke[];
  /** Ink is a line, and part of a line is still a line, so it is never whole-only. */
  wholeOnly(strokeId: string): boolean;
  /** Calls back with each stroke finished on this device, for sharing and saving. */
  onFinished(handler: (stroke: Stroke) => void): () => void;
  /** More readings for the stroke in hand — a whole coalesced batch at once. */
  extend(samples: readonly InkSample[]): void;
  /** The pointer lifted: the stroke joins the board. */
  finish(): void;
  /** The pointer was cancelled: the stroke is dropped. */
  cancel(): void;
  /** SRS C1 - empties this screen's picture of the page, for turning to another. */
  clear(): void;
  /**
   * SRS C8 - nothing. Ink has no z-order of its own: a line goes over what was there
   * when it was drawn, and that is the whole of it.
   */
  reorder(): readonly BoardObject[];
  paintStatic(ctx: CanvasRenderingContext2D, viewport: ViewportState): void;
  paintActive(ctx: CanvasRenderingContext2D, viewport: ViewportState): void;
  readonly strokes: readonly Stroke[];
  /**
   * The strokes whose boxes touch an area, in the order they are painted.
   *
   * Answered from the spatial index rather than by walking the board, which is what
   * lets the eraser hit-test on a three-hour board without dropping frames
   * (docs/03-ARCHITECTURE.md §7.4).
   */
  near(area: Rect): readonly Stroke[];
  /** The stroke with this id, or null if the board no longer holds it. */
  find(strokeId: string): Stroke | null;
  /** Canvas-space box around everything drawn, or null on an empty board. */
  contentBounds(): Rect | null;
  /**
   * Forgets what the board layer already holds, so the next paint draws it again.
   *
   * Needed when something that is not ink changes on that same layer — a shape moved
   * or rubbed out. Paint cannot be lifted off a canvas, so the only way to take
   * something away is to draw the whole layer again without it.
   */
  invalidateBoard(): void;
  readonly drawing: boolean;
  /** True once the device has proved it reports no pressure (step 2.2 fallback). */
  readonly usingVelocity: boolean;
}

interface Live {
  readonly points: InkPoint[];
  readonly canvasWidth: number;
  /** Frozen when the stroke starts, so changing colour mid-line cannot rewrite it. */
  readonly pen: PenChoice;
  /** SRS C8 — and neither can reaching for another layer halfway through it. */
  readonly layer: Layer;
}

interface Settings {
  readonly userId: string;
  readonly targetScreenWidth: () => number;
  readonly pen: () => PenChoice;
  readonly layer: () => Layer;
  readonly smooth: (points: readonly InkPoint[]) => readonly InkPoint[];
}

interface SessionState extends Omit<BoardInk, 'board'> {
  /** What the board canvas already holds, band by band. Replaced when it is emptied. */
  board: BoardMemory;
  readonly pressure: PressureReader;
  readonly settings: Settings;
  readonly onChanged: (layer: CanvasLayerName) => void;
  live: Live | null;
  /** Counts strokes, so a new one is never mistaken for more of the last one. */
  strokeSeq: number;
  /** Told about every stroke finished here, so it can be shared and saved. */
  readonly finishedHandlers: Set<(stroke: Stroke) => void>;
  readonly painted: PaintMemory;
}

/** Turns collected points into the frozen stroke record (docs/05-DATA-MODEL.md §3.1). */
function toStroke(live: Live, points: readonly InkPoint[], options: Settings): Stroke {
  const flat = new Float32Array(points.length * 3);
  points.forEach((point, index) => {
    flat[index * 3] = point.x;
    flat[index * 3 + 1] = point.y;
    flat[index * 3 + 2] = point.pressure;
  });
  return {
    id: newStrokeId(),
    userId: options.userId,
    tool: live.pen.tool,
    points: flat,
    canvasWidth: live.canvasWidth,
    color: live.pen.color,
    opacity: live.pen.opacity,
    layer: live.layer,
    createdAt: Date.now(),
  };
}

/** Stores the pressure this sample should carry: reported, or derived from speed. */
function record(state: SessionState, sample: InkSample): InkPoint {
  return {
    x: sample.x,
    y: sample.y,
    pressure: state.pressure.read({
      x: sample.x,
      y: sample.y,
      pressure: sample.pressure,
      t: sample.at,
    }),
  };
}

function begin(state: SessionState, sample: InkSample, viewport: ViewportState): void {
  // SRS C3 — the width is fixed at the zoom the stroke started at and never changes
  // afterwards, which is why the stored value is in canvas units.
  state.strokeSeq += 1;
  state.live = {
    points: [record(state, sample)],
    canvasWidth: screenWidthToCanvasWidth(toViewport(viewport), state.settings.targetScreenWidth()),
    pen: state.settings.pen(),
    layer: state.settings.layer(),
  };
  state.onChanged('active');
}

function extend(state: SessionState, samples: readonly InkSample[]): void {
  const live = state.live;
  if (!live || samples.length === 0) return;
  for (const sample of samples) live.points.push(record(state, sample));
  state.onChanged('active');
}

function finish(state: SessionState): void {
  const live = state.live;
  if (!live) return;
  if (live.points.length > 0) {
    // SRS C5 — the jitter comes out as the hand lifts, never while it is down.
    const stroke = toStroke(live, state.settings.smooth(live.points), state.settings);
    land(state, stroke);
    // Sharing and saving happen after the ink is already down, never before it.
    for (const handler of state.finishedHandlers) handler(stroke);
  }
  state.live = null;
  state.onChanged('static');
  state.onChanged('active');
}

/** Ink from somebody else. It lands the same way and repaints the same layer. */
function accept(state: SessionState, stroke: Stroke): void {
  if (state.byId.has(stroke.id)) return;
  land(state, stroke);
  state.onChanged('static');
}

/** Takes a stroke off the board and asks for the layer to be drawn again without it. */
/** Listens for strokes finished on this device, and hands back the way to stop. */
function watchFinished(state: SessionState, handler: (stroke: Stroke) => void): () => void {
  state.finishedHandlers.add(handler);
  return () => {
    state.finishedHandlers.delete(handler);
  };
}

/** A second copy of a stroke, offset, and already on the board. */
function duplicate(state: SessionState, stroke: Stroke, offset: number): Duplicated {
  const copy = duplicateStroke(stroke, offset);
  accept(state, copy);
  return { view: copy, stroke: copy, object: null };
}

/** §16 — a held copy of a stroke, put back on the board as new ink. */
function place(state: SessionState, item: Copied, offset: number): Duplicated | null {
  if (!item.stroke) return null;
  return duplicate(state, item.stroke, offset);
}

function remove(state: SessionState, strokeId: string): boolean {
  if (!removeStrokeFrom(state, strokeId)) return false;
  state.onChanged('static');
  return true;
}

/** Swaps a stroke for a changed version of itself, and repaints the layer. */
function replace(state: SessionState, stroke: Stroke): boolean {
  if (!replaceStrokeIn(state, stroke)) return false;
  state.onChanged('static');
  return true;
}

function cancel(state: SessionState): void {
  state.live = null;
  state.onChanged('active');
}

/**
 * Empties the board (SRS C1 — turning to another page).
 *
 * Nothing is deleted by this: the ink lives in the shared document, and what is
 * emptied is only this screen's picture of one page. The page's own strokes are
 * handed back the moment the new page is taken up.
 */
function clear(state: SessionState): void {
  state.finished.length = 0;
  for (const band of state.bands) band.length = 0;
  state.byId.clear();
  state.order.clear();
  state.index = newIndex();
  state.drawn = null;
  state.live = null;
  state.board = emptyBoardMemory();
  state.onChanged('static');
  state.onChanged('active');
}

function paintActive(
  state: SessionState,
  ctx: CanvasRenderingContext2D,
  viewport: ViewportState,
): void {
  const live = state.live;
  if (!live) {
    // The stroke has landed on the board; the active layer must not keep a copy of it.
    clearCanvas(ctx);
    state.painted.points = 0;
    state.painted.key = null;
    return;
  }

  const flat = isFlatTool(live.pen.tool);
  const key = viewportKey(viewport, state.strokeSeq);
  const plan = planActivePaint(state.painted, key, live.pen.opacity === 1 && !flat);
  if (plan.clear) clearCanvas(ctx);

  drawInk(
    ctx,
    live.points,
    {
      color: live.pen.color,
      opacity: live.pen.opacity,
      canvasWidth: live.canvasWidth,
      flat,
    },
    toViewport(viewport),
    plan.from,
  );

  state.painted.points = live.points.length;
  state.painted.key = key;
}

/** The pen's settings, with the optional ones filled in. */
function settingsFrom(options: InkSessionOptions): Settings {
  return {
    userId: options.userId,
    targetScreenWidth: options.targetScreenWidth,
    pen: options.pen,
    layer: options.layer ?? (() => 'content'),
    smooth: options.smooth ?? ((points) => points),
  };
}

/** An empty board, with nothing drawn and nobody listening yet. */
function emptyState(options: InkSessionOptions): SessionState {
  return {
    finished: [],
    bands: newBands(),
    board: emptyBoardMemory(),
    byId: new Map<string, Stroke>(),
    order: new Map<string, number>(),
    index: newIndex(),
    drawn: null,
    pressure: createPressureReader(),
    live: null,
    strokeSeq: 0,
    finishedHandlers: new Set<(stroke: Stroke) => void>(),
    painted: { points: 0, key: null },
    onChanged: options.onChanged,
    settings: settingsFrom(options),
  };
}

/** §16 — the clipboard's view of the ink: taking a copy, and putting one back. */
function clipboardParts(state: SessionState): Pick<InkSession, 'copyOf' | 'place' | 'all'> {
  return {
    copyOf: (strokeId) => {
      const stroke = findStroke(state, strokeId);
      return stroke ? { stroke, object: null } : null;
    },
    place: (item, offset) => place(state, item, offset),
    all: () => state.finished,
  };
}

/** What each canvas layer draws, for the board that owns them. */
function paintParts(state: SessionState): Pick<InkSession, 'paintStatic' | 'paintActive'> {
  return {
    paintStatic: (ctx, viewport) => {
      paintBoard(state, ctx, toViewport(viewport));
    },
    paintActive: (ctx, viewport) => {
      paintActive(state, ctx, viewport);
    },
  };
}

export function createInkSession(options: InkSessionOptions): InkSession {
  const state = emptyState(options);

  return {
    get strokes() {
      return state.finished;
    },
    near: (area) => strokesIn(state, area),
    find: (strokeId) => findStroke(state, strokeId),
    contentBounds: () => state.drawn,
    invalidateBoard: () => {
      state.board.key = null;
    },
    get drawing() {
      return state.live !== null;
    },
    get usingVelocity() {
      return state.pressure.usingVelocity;
    },
    begin: (sample, viewport) => {
      begin(state, sample, viewport);
    },
    accept: (stroke) => {
      accept(state, stroke);
    },
    duplicate: (stroke, offset) => duplicate(state, stroke, offset),
    ...clipboardParts(state),
    wholeOnly: () => false,
    reorder: () => [],
    replace: (stroke) => replace(state, stroke),
    remove: (strokeId) => remove(state, strokeId),
    onFinished: (handler) => watchFinished(state, handler),
    extend: (samples) => {
      extend(state, samples);
    },
    finish: () => {
      finish(state);
    },
    cancel: () => {
      cancel(state);
    },
    clear: () => {
      clear(state);
    },
    ...paintParts(state),
  };
}
